import React, { useRef } from 'react';

/**
 * LoadingCover — the white cover a page holds over its content frame while that frame is still
 * assembling itself.
 *
 * WHY THIS IS ONE COMPONENT AND NOT MARKUP IN EACH PAGE. The public tracking flow hands a single
 * wait between two components: PublicTracking covers the globe while it looks a WAL ID up, then
 * unmounts and ShareRadarOrder covers the map while it pulls the shipment and draws the route. When
 * each page built its own cover the handoff read as TWO loading screens — the visitor watched a
 * spinner appear, go, and a different one take its place (client, 2026-10-07). Sharing the markup is
 * what makes the swap invisible: same white, same spinner in the same place, and the same label when
 * the caller carries one across.
 *
 * SPINNER CONTINUITY is the whole reason for the negative animation-delay below. Tailwind's
 * `animate-spin` is a 1s loop that starts at 0deg on every freshly mounted element, so the second
 * cover's spinner would snap back to the top mid-rotation — the one frame that still gives the swap
 * away. Offsetting the new animation by how far into a second the page clock already is starts it at
 * the angle the outgoing spinner was at, so the rotation runs unbroken across the unmount. The offset
 * is frozen in a ref: recomputing it on a re-render would restart the animation, which is the exact
 * jump this avoids.
 */
const SPIN_DURATION_MS = 1000;

/** up = opaque and blocking; fading = transitioning out. The owner drops the cover after the fade. */
export type LoadingCoverPhase = 'up' | 'fading';

interface ILoadingCoverProps {
    /** What is being waited on, e.g. "Looking up WAL-XXXX-123…". Read out via aria-live. */
    label: string;
    phase: LoadingCoverPhase;
    /** Fade length in ms. Owned by the caller so its own transitions can run to the same beat. */
    fadeMs: number;
    /** Per-frame stacking and rounding — the globe frame has nothing above it, the map has controls. */
    className?: string;
}

const LoadingCover = ({ label, phase, fadeMs, className = '' }: ILoadingCoverProps) => {
    const spinOffsetMs = useRef(Math.round(performance.now() % SPIN_DURATION_MS)).current;

    return (
        <div
            className={`absolute inset-0 bg-white flex flex-col items-center justify-center gap-y-3 transition-opacity ease-out ${phase === 'fading' ? 'opacity-0' : 'opacity-100'} ${className}`}
            style={{ transitionDuration: `${fadeMs}ms` }}
            aria-live="polite"
            aria-busy={phase === 'up'}
        >
            <div className="w-8 h-8 border-2 border-utilityGray200 border-t-primary rounded-full animate-spin" style={{ animationDelay: `-${spinOffsetMs}ms` }} />
            <p className="text-grayLight600 text-sm font-medium">{label}</p>
        </div>
    );
};

export default LoadingCover;
